import { Constants } from "../../Application/Constants";
import { AbstractDataSource } from "../AbstractDataSource";
import { ITokenSource } from "../ITokenSource";
import { ApiError, IApiOptions, IXrmDataSource } from "./IXrmDataSource";
import { IMultipleRecordsXrmDataSource } from "./type";
/**
 * Represents a datasource connecting directly to D365 web api.
 */

export interface IMultipleRecord<T> {
    'odata.context': string;
    value: T[];
}

export class XrmDataSource extends AbstractDataSource implements IXrmDataSource {

    private readonly scopes: string[];
    private readonly baseUrl: string;
    private readonly version: string;

    constructor(tokenSource: ITokenSource, version: string = '9.1') {
        super(tokenSource);
        this.scopes = Constants.crm.scopes;
        this.baseUrl = Constants.crm.baseUrl;
        this.version = version;
    }

    // helpers

    protected async handleResponse(response: Response, parseResponse: boolean): Promise<any> {
        if (response.ok) {
            if (parseResponse) {
                if (response.status === 204) {
                    return null;
                }
                try {
                    return await response.json();
                } catch (err) {
                    throw new Error(`Failed to parse response: ${err}`);
                }
            }
        } else if (
            response.status === 400 ||
            response.status === 412) {
            // WebAPI returns a JSON body on 400 error (most of the time)
            let body = await response.json().catch((err) => {
                response.text().then((text) => {
                    return { error: { code: -1, message: text } };
                }).catch((err) => {
                    return { error: { code: -1, message: 'Failed to interpret response' } };
                });
            });
            throw new ApiError(
                response.status,
                `${response.status} ${body.error.code} ${body.error.message}`,
                body.error.code,
                body.error.message);
        } else {
            let text = await response.text().catch((err) => {
                return 'Failed to interpret response';
            });
            throw new ApiError(
                response.status,
                `${response.status} ${response.statusText} ${text}`);
        }
    }

    protected async send(method: string, uri: string, options?: IApiOptions): Promise<Response> {

        let caching: string | null = null;

        let cacheResponse: Response | null | undefined = null;

        // // For now disable Front End App cache, let service worker handle it
        // if (options?.additionalHeaders?.['x-gl-cache'])
        //     caching = uri;

        const cacheIsWithNetworkFallback = options?.additionalHeaders?.['x-gl-cache'] == 'cache-with-network-fallback';
        const cacheRevalidateDelay = options?.additionalHeaders?.['x-gl-cache-revalidate-delay'] ?? null;

        // Currently we are not using the cache, but we can use it if needed
        // Service Worker is handling the cache
        // -------------------------------------------------------
        if (caching) {
            const cacheManager = await caches.open('group-library');
            cacheResponse = await cacheManager.match(caching);

            // if loaded from cache, and we have a revalidate delay, lets revalidate the cache
            if (cacheResponse && cacheRevalidateDelay && ~cacheIsWithNetworkFallback) {
                setTimeout(async () => {
                    const tOptions = { ...options };

                    // making sure its a live request and no cache is used
                    tOptions.additionalHeaders = {
                        ...options?.additionalHeaders,
                        'x-gl-cache': null,
                        'x-gl-cache-revalidate-delay': null,
                    };
                    const response = await this.send(method, uri, tOptions);
                    cacheManager?.put(caching!, response);
                }, Number.parseInt(cacheRevalidateDelay));
            }

            if (cacheResponse) {
                return cacheResponse;
            }
        }

        let token = await this.getToken(this.scopes);

        // build the prefer header
        let prefer: string[] = [];

        // get formatted values
        if (options?.includeAnnotations)
            prefer.push(`odata.include-annotations="*"`);

        // for POST/PUT return the new record
        if (options?.returnRecord)
            prefer.push(`return=representation`);

        // for paging, set the page size
        if (options?.pageSize) {
            prefer.push(`odata.maxpagesize=${options.pageSize}`);
        }

        // build the request
        let request: RequestInit = {
            method: method,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json; charset=utf-8',
                'OData-MaxVersion': '4.0',
                'OData-Version': '4.0',
                'Prefer': prefer.join(', '),
                ...(options?.additionalHeaders || {})
            },
            signal: options?.controller?.signal,
        };

        if (options?.noAccept) {
            // @ts-ignore
            delete request.headers['Accept'];
        }

        // set the body if given
        if (options?.body) {
            request.body = options?.body;
        }

        const response = await fetch(uri, request);

        // if not loaded from cache, lets cache it
        if (caching)
            if (!cacheResponse && !cacheIsWithNetworkFallback) {
                const cacheManager = await caches.open('group-library');
                cacheResponse = response.clone();
                cacheManager.put(caching, cacheResponse!);
            }

        return response;
    }

    // public interface

    getBaseUrl(): string {
        return `${this.baseUrl}/api/data/v${this.version}`;
    }

    async raw(method: string, url: string, options?: IApiOptions): Promise<Response> {
        let uri = `${this.baseUrl}/api/data/v${this.version}/${url}`;
        let response = await this.send(method, uri, options);
        await this.handleResponse(response, false);
        return response;
    }


    async execute(method: string, url: string, options?: IApiOptions): Promise<any> {

        let uri = `${this.baseUrl}/api/data/v${this.version}/${url}`;
        const response = await this.send(method, uri, options);
        return await this.handleResponse(response, true);
    }

    async page(method: string, url: string, options?: IApiOptions): Promise<any> {
        // because this method is expecting a full URL from nextLink, don't do any replace stuff
        let response = await this.send(method, url, options);
        return await this.handleResponse(response, true);
    }

    async whoAmI(): Promise<any> {
        return await this.execute('GET', 'WhoAmI');
    }


    // helpers

    private async get(query: string, options?: IApiOptions): Promise<any> {
        let data = await this.execute("GET", `${query}`, options);
        return data.value;
    }

    // public interface


    async query(query: string, options?: IApiOptions): Promise<any[]> {
        return await this.get(query, options);
    }

    async queryAll(query: string, pageCount: number): Promise<any[]> {
        let records = [];
        let data = await this.execute("GET", `${query}`, {
            pageSize: pageCount
        });
        records.push(...data.value);
        let nextLink: string | null = data["@odata.nextLink"] as string || null;
        while (nextLink) {
            data = await this.page("GET", nextLink, {
                pageSize: pageCount
            });
            records.push(...data.value);
            nextLink = data["@odata.nextLink"] as string || null;
        }
        return records;
    }



    async fetch<T>(collectionName: string, query: string, options?: IApiOptions): Promise<T[]> {
        // https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/retrieve-and-execute-predefined-queries#use-custom-fetchxml
        return await this.get(`${collectionName}?fetchXml=${encodeURIComponent(query)}`, options);
    }

    async view(viewId: string): Promise<any[]> {
        // https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/retrieve-and-execute-predefined-queries#predefined-queries
        return await this.get(`userQuery=${viewId}`);
    }

    async getRecord<T>(collectionName: string, id: string, fields?: string, options?: IApiOptions): Promise<T> {
        // https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/retrieve-entity-using-web-api#retrieve-specific-properties
        let url = '';
        if (fields) {
            url = `${collectionName}(${id})?$select=${fields}`;
        } else {
            url = `${collectionName}(${id})`;
        }
        return await this.execute("GET", url, options);
    }

    async getRecords<T>(collectionName: string, filter?: string, fields?: string, options?: IApiOptions, orderBy?: string): Promise<IMultipleRecordsXrmDataSource<T>> {
        let url = `${collectionName}`;
        let queryParameters = [];

        if (fields) {
            queryParameters.push(`$select=${fields}`);
        }

        if (filter) {
            queryParameters.push(`$filter=${filter}`);
        }

        if (orderBy) {
            queryParameters.push(`$orderby=${orderBy}`);
        }

        if (queryParameters.length > 0) {
            url += `?${queryParameters.join('&')}`;
        }

        return await this.execute("GET", url, options);
    }

    async getRecordProperty(collectionName: string, id: string, field: string): Promise<any> {
        // https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/retrieve-entity-using-web-api#retrieve-a-single-property-value
        let data = await this.get(`${collectionName}(${id})/${field}`);
        return data.value;
    }

    async getRecordLookupProperty(collectionName: string, id: string, field: string, lookupFields?: string): Promise<any> {
        // https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/retrieve-entity-using-web-api#retrieve-navigation-property-values
        if (lookupFields)
            return await this.get(`${collectionName}(${id})/${field}?$select=${lookupFields}`);

        return await this.get(`${collectionName}(${id})/${field}`);
    }

    async create(collectionName: string, data: any): Promise<void> {
        // https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/create-entity-web-api#basic-create
        await this.raw("POST", `${collectionName}`, {
            body: JSON.stringify(data)
        });
    }

    async createAndReturn(collectionName: string, data: any, fields?: string[]): Promise<any> {
        let select = '';
        if (fields && fields.length > 0) {
            select = `?$select=${fields.join(',')}`;
        }
        // https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/create-entity-web-api#basic-create
        return await this.execute("POST", `${collectionName}${select}`, {
            includeAnnotations: true,
            returnRecord: true,
            body: JSON.stringify(data)
        });
    }

    async update(collectionName: string, id: string, data: any, version?: number | string): Promise<void> {
        // https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/update-delete-entities-using-web-api#basic-update
        let headers: any = {};

        if (version && version === '*') {
            headers['If-Match'] = `*`;
        }
        else if (version) {
            headers['If-Match'] = `W/"${version}"`;
        }

        return await this.execute(
            'PATCH',
            `${collectionName}(${id})`,
            {
                additionalHeaders: headers,
                body: JSON.stringify(data)
            });
    }

    async updateAndReturn(collectionName: string, id: string, data: any, fields?: string[], version?: number | string) {
        let select = '';
        if (fields && fields.length > 0) {
            select = `?$select=${fields.join(',')}`;
        }
        // https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/update-delete-entities-using-web-api#basic-update
        let headers: any = {};
        if (version && version === '*') {
            headers['If-Match'] = `*`;
        }
        else if (version) {
            headers['If-Match'] = `W/"${version}"`;
        }

        return await this.execute('PATCH', `${collectionName}(${id})${select}`, {
            additionalHeaders: headers,
            returnRecord: true,
            body: JSON.stringify(data)
        });
    }

    async updateField(collectionName: string, id: string, property: string, value: any, version?: number): Promise<void> {
        // https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/update-delete-entities-using-web-api#update-a-single-property-value
        let headers: any = {};
        if (version) {
            headers['If-Match'] = `W/"${version}"`;
        }
        return await this.execute('PUT', `${collectionName}(${id})/${property}`, {
            additionalHeaders: headers,
            body: { value: value }
        });
    }

    async clearField(collectionName: string, id: string, property: string, version?: number): Promise<void> {
        // https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/update-delete-entities-using-web-api#delete-a-single-property-value
        let headers: any = {};
        if (version) {
            headers['If-Match'] = `W/"${version}"`;
        }
        await this.raw('DELETE', `${collectionName}(${id})/${property}/$ref`, {
            additionalHeaders: headers
        });
    }

    async associate(collectionName: string, id: string, relationship: string, other: { id: string, collectionName: string }): Promise<void> {
        // https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/associate-disassociate-entities-using-web-api#add-a-reference-to-a-collection-valued-navigation-property
        await this.raw('POST', `${collectionName}(${id})/${relationship}/$ref`, {
            body: {
                '@odata.id': `${this.getBaseUrl()}/${other.collectionName}(${other.id})`
            }
        });
    }

    async disassociate(collectionName: string, id: string, relationship: string, otherId: string): Promise<void> {
        // https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/associate-disassociate-entities-using-web-api#remove-a-reference-to-an-entity
        await this.raw('DELETE', `${collectionName}(${id})/${relationship}(${otherId})/$ref`);
    }

    async delete(collectionName: string, id: string): Promise<void> {
        // https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/update-delete-entities-using-web-api#basic-delete
        await this.raw('DELETE', `${collectionName}(${id})`);
    }

    // Deprecated: This method is no longer used
    async downloadFileAttribute(collectionName: string, id: string, attribute: string): Promise<Uint8Array> {
        let chunkSize = 4 * 1024 * 1024;

        let resp = await this.raw('GET', `${collectionName}(${id})/${attribute}/$value`, {
            additionalHeaders: {
                'Range': `bytes=0-${chunkSize - 1}`
            }
        });

        let fileSize = parseInt(resp.headers.get('x-ms-file-size')!, 10);
        let file = new Uint8Array(fileSize);
        // let nextRequest = resp.headers.get('Location');

        // try adding BOM/Encoding here helped?
        // BOM : U+FEFF
        // UTF8  : 0xEF,0xBB,0xBF
        // UTF16 : 0xFE,0xFF,0x00,0x00.

        let buff = await resp.arrayBuffer();
        let temp = new Uint8Array(buff);

        let chunkSegment = 0;
        file.set(temp, chunkSegment);

        let totalBytes = buff.byteLength;
        while (totalBytes < fileSize) {
            ++chunkSegment;
            resp = await this.raw('GET', `${collectionName}(${id})/${attribute}/$value`, {
                additionalHeaders: {
                    'Range': `bytes=${totalBytes}-${Math.min(totalBytes + chunkSize - 1, fileSize)}`
                }
            });
            buff = await resp.arrayBuffer();
            temp = new Uint8Array(buff);
            file.set(temp, chunkSize * chunkSegment);
            totalBytes += buff.byteLength;
        }

        return file;
    }

    // we ran into an issue of loading data. TextDecoder not properly formatting the document.
    async downloadFileAttributeBlob(collectionName: string, id: string, attribute: string) {

        let chunkSize = 4 * 1024 * 1024;
        const url = `${collectionName}(${id})/${attribute}/$value`;

        let resp = await this.raw('GET', url, {
            additionalHeaders: {
                'Range': `bytes=0-${chunkSize - 1}`,

                // hack/work around:
                // we are sending a ranged request, but the cache will respond with the full file
                // since this is a partial response, we can't rely on the send cache method
                // how ever it should not cache anything, we will manually cache this once we have all the chunks
                // we signal the send cache method to only respond with cache if it has, otherwise ignore
                'x-gl-cache': 'cache-with-network-fallback',
            },
            noAccept: true
        });

        const msFileSize = resp.headers.get('x-ms-file-size') ?? "0";
        const msFileName = resp.headers.get('x-ms-file-name') ?? "";

        const fileSize = parseInt(msFileSize, 10);

        let buff = await resp.blob();
        let blobs: Blob[] = [];
        blobs.push(buff);

        // partial response, lets download all the chunks
        if (resp.status === 206) {
            // let nextRequest = resp.headers.get('Location');
            let totalBytes = buff.size;
            while (totalBytes < fileSize) {

                resp = await this.raw('GET', url, {
                    additionalHeaders: {
                        'Range': `bytes=${totalBytes}-${Math.min(totalBytes + chunkSize - 1, fileSize)}`
                    },
                    noAccept: true
                });

                blobs.push(await resp.blob());

                totalBytes = blobs.reduce((prev, curr) => {
                    return prev + curr.size
                }, 0);
            }
        }

        const blob = new Blob(blobs);

        // manually caching
        // if loaded from cache, no need to cache again
        if (!resp.headers.has('x-gl-cache')) {

            // Service Worker Cache
            // ------------------------------------------------------
            navigator.serviceWorker.ready.then((registration) => {
                registration?.active?.postMessage({
                    type: 'cache_add_blob',
                    url: `${this.baseUrl}/api/data/v${this.version}/${url}`,
                    headers: {
                        'x-ms-file-size': msFileSize,
                        'x-ms-file-name': msFileName,
                        'Content-Type': 'application/octet-stream',
                        'Content-Length': fileSize.toString()
                    },
                    blob: blob
                });
            });

            // // Application Cache
            // // ------------------------------------------------------
            // const caching = `${this.baseUrl}/api/data/v${this.version}/${url}`;
            // if (caching) {
            //     const cacheManager = await caches.open('group-library');
            //     const cacheRequest = new Request(caching);
            //     const cacheResponseHeaders = new Headers();

            //     // mark the response as coming from the cache
            //     cacheResponseHeaders.append('x-gl-cache', 'cache-always');
            //     cacheResponseHeaders.append('x-ms-file-size', msFileSize);
            //     cacheResponseHeaders.append('x-ms-file-name', msFileName);
            //     cacheResponseHeaders.append('Content-Type', 'application/octet-stream');
            //     cacheResponseHeaders.append('Content-Length', fileSize.toString());

            //     const cacheResponse = new Response(
            //         blob,
            //         { headers: cacheResponseHeaders }
            //     );

            //     cacheManager?.put(caching!, cacheResponse);
            // }
        }

        return {
            blob: blob,
            fileName: msFileName,
        };
    }

    async downloadFileAttributeBase64(collectionName: string, id: string, attribute: string): Promise<string> {
        let data = await this.downloadFileAttribute(collectionName, id, attribute);

        // build a base64 URL for the data
        // https://stackoverflow.com/a/66046176
        const base64url: string = await new Promise((r) => {
            const reader = new FileReader();
            reader.onload = () => r(reader.result as string);
            reader.readAsDataURL(new Blob([data]));
        });
        // format is "data:application/octet-stream;base64,<your base64 data>"
        let base64: string = base64url.split(",", 2)[1];
        return base64;
    }

    async uploadFileAttribute(collectionName: string, id: string, attribute: string, content: Uint8Array): Promise<any> {
        let token = await this.getToken(this.scopes);

        // build the request
        let request: any = {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': '*/*',
                'Content-Type': 'application/octet-stream',
                'OData-MaxVersion': '4.0',
                'OData-Version': '4.0',
                'x-ms-file-name': 'snapshot.json.zip'
            }
        };

        request.body = content;

        let response = await fetch(`${this.baseUrl}/api/data/v${this.version}/${collectionName}(${id})/${attribute}`, request);
        await this.handleResponse(response, false);
        return response;
    }

    async uploadFileAttributeBlob(collectionName: string, id: string, attribute: string, content: Blob, fileName: string) {
        let token = await this.getToken(this.scopes);

        // build the request
        let request: any = {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': '*/*',
                'Content-Type': 'application/octet-stream',
                'OData-MaxVersion': '4.0',
                'OData-Version': '4.0',
                'x-ms-file-name': fileName
            }
        };

        request.body = content;

        let response = await fetch(`${this.baseUrl}/api/data/v${this.version}/${collectionName}(${id})/${attribute}`, request);
        await this.handleResponse(response, false);
        return response;
    }

    async uploadFileAttributeBase64(collectionName: string, id: string, attribute: string, content: string): Promise<any> {
        // https://stackoverflow.com/a/54123275
        let dataUrl = "data:application/octet-binary;base64," + content;
        let data = await fetch(dataUrl)
            .then(res => res.arrayBuffer())
            .then(buffer => {
                return new Uint8Array(buffer);
            });

        return await this.uploadFileAttribute(collectionName, id, attribute, data);
    }

    async deleteFileAttribute(collectionName: string, id: string, attribute: string): Promise<any> {
        await this.raw("DELETE", `${collectionName}(${id})/${attribute}`);
    }

    async getEntityMetadata(logicalName: string, query: string): Promise<any> {
        const path = `EntityDefinitions(LogicalName='${logicalName}')${query}`;

        return await this.execute('GET', path, {
            additionalHeaders: {
                'x-gl-cache': 'cache-always',
            }
        });
    }

    async queryBatch(query: string): Promise<any[]> {
        const records = [];
        let baseUrl = this.getBaseUrl();
        const url = `${baseUrl}/${query}`;

        // get the first page
        let parsedData = await this.executeBatchQuery(url);
        records.push(...parsedData.value);
        let nextLink = parsedData["@odata.nextLink"] as string || null;

        // now iterate any otehr pages
        while (nextLink) {
            parsedData = await this.executeBatchQuery(nextLink);
            records.push(...parsedData.value);
            nextLink = parsedData["@odata.nextLink"] as string || null;
        }

        return records;
    }

    private async executeBatchQuery(url: string): Promise<any> {
        var bodyFetch = '';
        bodyFetch = '--batch_recordfetch\n';
        bodyFetch += 'Content-Type: application/http\n';
        bodyFetch += 'Content-Transfer-Encoding: binary\n'
        bodyFetch += '\n';
        bodyFetch += `GET ${url} HTTP/1.1\n`;
        bodyFetch += 'Accept: application/json\n';
        bodyFetch += 'OData-Version: 4.0\n';
        bodyFetch += 'OData-MaxVersion: 4.0\n';
        bodyFetch += 'Prefer: odata.include-annotations=*\n';
        bodyFetch += '\n';
        bodyFetch += '--batch_recordfetch--';

        let token = await this.getToken(this.scopes);
        let response = await fetch(`${this.getBaseUrl()}/$batch`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json',
                'OData-MaxVersion': '4.0',
                'OData-Version': '4.0',
                'Prefer': 'odata.include-annotations="*"',
                'Content-Type': 'multipart/mixed;boundary=batch_recordfetch'
            },
            body: bodyFetch
        });

        await this.handleResponse(response, false);
        let responseText = await response.text();
        let responseLines: string[] = responseText.split("\n");

        // Take 3rd to last line split to and deserialize JSON to object
        var data = responseLines[responseLines.length - 3];

        return JSON.parse(data);
    }

    async createProcess(data: any): Promise<void> {
        await this.create('bcbs_grouplibraryprocesses', data)
    }

}